// AdminAudiencesPage.tsx — (tf3-42, B90; planning 10-21 16:45 #2–#6) Admin → Audiences: the backend's own audience
// profiles (admins only, as its API). The list (label, id, parent, the merged age band; the child band's words); create
// and edit with Inherit / None / a value on every axis, the values from @vms/tubeforge-shared's lists; Preview of the
// merged chain after each save, its problems in amber (the save stands); delete with a confirm, the 409 in words.
import { useCallback, useEffect, useRef, useState } from "react";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { audiencesApi, type ProfileRow } from "@/api/audiences.ts";
import { AXIS_FIELDS, CHILD_LABEL, bandLabel, isChild, formOf, axesOf, PROFILE_ID, DELETE_NOTE, deleteErrorText, errorText,
  type FormState, type Mode } from "@/lib/audiences.ts";

type Editing = { id: string; isNew: boolean; label: string; parentId: string; form: FormState };
const field = { display: "block", width: "100%", marginTop: 4 } as const;

function ModePick({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return <select value={mode} onChange={(e) => onChange(e.target.value as Mode)} style={{ marginLeft: 8 }}>
    <option value="inherit">Inherit</option><option value="none">None</option><option value="value">A value</option></select>;
}

export function AdminAudiencesPage() {
  const [rows, setRows] = useState<ProfileRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ed, setEd] = useState<Editing | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ id: string; profile: Record<string, unknown>; problems: string[] } | null>(null);
  const [confirming, setConfirming] = useState<ProfileRow | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);
  const editSeq = useRef(0);   // (audit 10-21 F4) the last Edit clicked wins

  const load = useCallback(() => {
    audiencesApi.list().then((r) => { setRows(r.profiles); setErr(null); }).catch((e) => setErr(errorText(e)));   // catch-reason: shown in place of the list
  }, []);
  useEffect(load, [load]);

  const showPreview = async (id: string) => {
    try { const r = await audiencesApi.resolve(id); setPreview({ id, ...r }); }
    catch (e) { setPreview({ id, profile: {}, problems: [errorText(e)] }); }   // catch-reason: an unresolvable chain is itself a problem to show
  };
  const edit = async (p: ProfileRow | null) => {
    const seq = ++editSeq.current;
    setSaveErr(null); setPreview(null);
    if (!p) { setEd({ id: "", isNew: true, label: "", parentId: "", form: formOf(null) }); return; }
    try { const r = await audiencesApi.get(p.id); if (seq !== editSeq.current) return; setEd({ id: p.id, isNew: false, label: r.profile.label, parentId: r.profile.parentId ?? "", form: formOf(r.profile) }); }
    catch (e) { setErr(errorText(e)); }   // catch-reason: shown above the list
  };
  const save = async () => {
    if (!ed) return;
    const a = axesOf(ed.form);
    if ("error" in a) { setSaveErr(a.error); return; }
    const parentId = ed.parentId || null;
    try {
      if (ed.isNew) await audiencesApi.create({ id: ed.id, label: ed.label.trim(), parentId, axes: a.axes });
      else await audiencesApi.update(ed.id, { label: ed.label.trim(), parentId, axes: a.axes });
      const id = ed.id; setEd(null); load(); await showPreview(id);   // the save stands; Preview shows any narrowing problem
    } catch (e) { setSaveErr(errorText(e)); }   // catch-reason: the API's 400/422 shown on the form
  };
  const remove = async (p: ProfileRow) => {
    try { await audiencesApi.remove(p.id); setConfirming(null); setDelErr(null); if (preview?.id === p.id) setPreview(null); if (ed?.id === p.id) setEd(null); load(); }
    catch (e) { setDelErr(deleteErrorText(e)); }   // catch-reason: shown in the confirm
  };
  const setAxis = (key: string, patch: Partial<FormState[string]>) => setEd((x) => x && ({ ...x, form: { ...x.form, [key]: { ...x.form[key], ...patch } } as FormState }));

  return (
    <PageContainer title="Audiences" subtitle="The audience profiles slates and pilots are made for. Each inherits from its parent."
      action={!ed && <Btn onClick={() => edit(null)}>New profile</Btn>}>
      {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}
      {preview && <TfCard>
        <h3 style={{ marginTop: 0 }}>Preview: {preview.id}, merged along its chain</h3>
        {preview.problems.length > 0 && <ul style={{ color: "#92400e", fontSize: 13, paddingLeft: 18 }}>{preview.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        <pre style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{JSON.stringify(preview.profile, null, 2)}</pre>
        <Btn variant="ghost" onClick={() => setPreview(null)}>Close</Btn>
      </TfCard>}
      {ed && <TfCard>
        <h3 style={{ marginTop: 0 }}>{ed.isNew ? "New profile" : `Edit ${ed.id}`}</h3>
        {ed.isNew && <label style={{ display: "block" }}>Id (lowercase letters, digits and _)
          <input style={field} value={ed.id} onChange={(e) => setEd({ ...ed, id: e.target.value })} /></label>}
        <label style={{ display: "block", marginTop: 8 }}>Label<input style={field} value={ed.label} onChange={(e) => setEd({ ...ed, label: e.target.value })} /></label>
        <label style={{ display: "block", marginTop: 8 }}>Parent
          <select style={field} value={ed.parentId} onChange={(e) => setEd({ ...ed, parentId: e.target.value })}>
            <option value="">None (a root)</option>
            {(rows ?? []).filter((r) => r.id !== ed.id).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select></label>
        {AXIS_FIELDS.map((a) => { const s = ed.form[a.key]; return <div key={a.key} style={{ marginTop: 10 }}>
          <strong style={{ fontSize: 13 }}>{a.label}</strong><ModePick mode={s.mode} onChange={(mode) => setAxis(a.key, { mode })} />
          {s.mode === "value" && (a.kind === "select"
            ? <select style={field} value={s.text} onChange={(e) => setAxis(a.key, { text: e.target.value })}>
                <option value="">Choose…</option>{a.values!.map((v) => <option key={v} value={v}>{a.key === "ageBand" ? bandLabel(v) : v}</option>)}</select>
            : a.kind === "lines"
              ? <textarea style={field} rows={3} placeholder="One per line" value={s.text} onChange={(e) => setAxis(a.key, { text: e.target.value })} />
              : <input style={field} value={s.text} onChange={(e) => setAxis(a.key, { text: e.target.value })} />)}
        </div>; })}
        <div style={{ marginTop: 10 }}>
          <strong style={{ fontSize: 13 }}>Visual world</strong><ModePick mode={ed.form.visualWorld.mode} onChange={(mode) => setAxis("visualWorld", { mode })} />
          {ed.form.visualWorld.mode === "value" && <>
            <textarea style={field} rows={3} placeholder="Settings, one per line" value={ed.form.visualWorld.text} onChange={(e) => setAxis("visualWorld", { text: e.target.value })} />
            <input style={field} placeholder="Aesthetic" value={ed.form.visualWorld.aesthetic} onChange={(e) => setAxis("visualWorld", { aesthetic: e.target.value } as never)} /></>}
        </div>
        {saveErr && <div style={{ color: "#b91c1c", marginTop: 8 }}>{saveErr}</div>}
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <Btn onClick={save} disabled={(ed.isNew && !PROFILE_ID.test(ed.id)) || ed.label.trim().length < 3}>Save</Btn>
          <Btn variant="ghost" onClick={() => setEd(null)}>Cancel</Btn></div>
      </TfCard>}
      {!rows && !err && <div className="tf-muted">Reading…</div>}
      {rows && <TfCard>
        {rows.length === 0 && <div className="tf-muted">No profiles yet.</div>}
        <table style={{ fontSize: 13, width: "100%", borderCollapse: "collapse" }}><tbody>
          {rows.map((p) => <tr key={p.id} style={{ borderTop: "1px solid #e5e7eb" }}>
            <td style={{ padding: "6px 4px" }}><strong>{p.label}</strong><div className="tf-muted" style={{ fontSize: 12 }}>{p.id}{p.parentId ? ` · from ${p.parentId}` : ""}</div>
              {isChild(p.resolvedAgeBand) && <div style={{ fontSize: 12, color: "#92400e" }}>{CHILD_LABEL}</div>}
              {p.resolveError && <div style={{ fontSize: 12, color: "#92400e" }}>{p.resolveError}</div>}
              {confirming?.id === p.id && <div style={{ fontSize: 12, marginTop: 6 }}>Delete {p.label} ({p.id})? {DELETE_NOTE}
                {delErr && <div style={{ color: "#b91c1c" }}>{delErr}</div>}
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}><Btn size="sm" onClick={() => remove(p)}>Delete</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => { setConfirming(null); setDelErr(null); }}>Cancel</Btn></div></div>}</td>
            <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
              <Btn size="sm" variant="ghost" onClick={() => showPreview(p.id)}>Preview</Btn>
              <Btn size="sm" variant="ghost" onClick={() => edit(p)}>Edit</Btn>
              <Btn size="sm" variant="ghost" onClick={() => { setConfirming(p); setDelErr(null); }}>Delete…</Btn></td>
          </tr>)}
        </tbody></table>
      </TfCard>}
    </PageContainer>
  );
}
